import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
	Link,
} from '@remix-run/react'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import { getErrorMessage } from '#app/utils/misc.tsx'
import { Brand } from './brand'
import PageGradient from './page-gradient'
import { Button } from './ui/button'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => JSX.Element | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<p>
			{error.status} {error.data}
		</p>
	),
	statusHandlers,
	unexpectedErrorHandler = error => <p>{getErrorMessage(error)}</p>,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => JSX.Element | null
}) {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)
	const params = useParams()

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	return (
		<div className="flex h-screen w-full flex-col items-center justify-between p-6">
			<Link to="/">
				<Brand variant="logo" />
			</Link>
			<div className="flex flex-col items-center gap-6">
				<p className="text-body-sm text-red-500">ERROR</p>
				<h1 className="text-center text-h1">
					Oh no!
					{isRouteErrorResponse(error)
						? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
								error,
								params,
							})
						: unexpectedErrorHandler(error)}
				</h1>
				<h4 className="max-w-[420px] text-center">
					Please try again. If this issue persists, please get in touch.
				</h4>
				<div className="flex gap-3">
					<Button className="w-[120px]" asChild>
						<Link to="/">Go home</Link>
					</Button>
					<Button variant="secondary" className="w-[120px]" asChild>
						<a href={'https://wt.ls/contact'}>Contact</a>
					</Button>
				</div>
			</div>
			<Brand variant="logo" className="opacity-0" />
			<PageGradient color="#ED2E2E" />
		</div>
	)
}
